/* RAF Pharmacy — custom cart drawer behaviour.
 * Works alongside the theme's cart-drawer.js / cart.js (does not replace them).
 *  - live delivery time ("Get it by 3 PM today")
 *  - quick add (+) on recommendation cards, using the drawer's own renderContents()
 *  - Shopify product recommendations ("auto" source) with collection fallback
 *  - WhatsApp order button with live cart contents
 * Re-initialises automatically whenever the theme re-renders the drawer.
 */
(() => {
  if (window.RAFDrawerCustomLoaded) return;
  window.RAFDrawerCustomLoaded = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ------------------------------------------------------------ money */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    format = format || '{{amount}}';
    const fmt = (n, decimals, thousands, decimal) => {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (frac ? decimal + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }
  const amountOnly = (format) => {
    const m = String(format || '').match(/\{\{\s*\w+\s*\}\}/);
    return m ? m[0] : '{{amount}}';
  };

  /* ------------------------------------------------------------ delivery time */
  function nowIn(tz, source) {
    if (source === 'device') {
      const deviceDate = new Date();
      return { h: deviceDate.getHours(), m: deviceDate.getMinutes() };
    }
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
      const get = (t) => Number(parts.find((p) => p.type === t).value);
      return { h: get('hour') % 24, m: get('minute') };
    } catch (e) {
      const d = new Date();
      return { h: d.getHours(), m: d.getMinutes() };
    }
  }
  function clock(mins, mode) {
    mins = ((mins % 1440) + 1440) % 1440;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (mode === '24') return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    return (h % 12 || 12) + (m ? ':' + String(m).padStart(2, '0') : '') + (h < 12 ? ' AM' : ' PM');
  }
  function updateDelivery(scope) {
    scope.querySelectorAll('[data-raf-delivery]').forEach((el) => {
      const d = el.dataset;
      const { h, m } = nowIn(d.tz || 'Asia/Qatar', d.timeSource || 'device');
      const hours = Number(d.hours) || 2;
      const open = Number(d.open) || 0;
      const cutoff = d.cutoff === undefined ? 24 : Number(d.cutoff);
      let base = h * 60 + m;
      let nextDay = false;
      if (h < open) base = open * 60;
      else if (h >= cutoff) {
        base = open * 60;
        nextDay = true;
      }
      let eta = base + hours * 60;
      if (d.round === 'hour') eta = Math.ceil(eta / 60) * 60;
      else if (d.round === 'half') eta = Math.ceil(eta / 30) * 30;
      if (eta >= 1440) nextDay = true;
      const text = nextDay ? d.tomorrow : d.today;
      const sub = el.querySelector('[data-raf-delivery-sub]');
      if (!sub || !text) return;
      const next = text.replace(/\{time\}/g, clock(eta, d.clock));
      if (sub.textContent !== next) sub.textContent = next;
    });
  }

  /* ------------------------------------------------------------ recommendations */
  const PLUS =
    '<svg class="raf-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 3v18M3 12h18"/></svg>';
  const recCache = new Map();
  const imgUrl = (src, w) => {
    if (!src) return '';
    const url = typeof src === 'string' ? src : src.src || src.url || '';
    return url + (url.includes('?') ? '&' : '?') + 'width=' + w;
  };
  function recCard(p, box) {
    const format = box.dataset.moneyFormat || '{{amount}}';
    const variants = p.variants || [];
    const v = variants.find((x) => x.available) || variants[0] || {};
    const single = variants.length === 1;
    const price = v.price != null ? v.price : p.price;
    const compare = v.compare_at_price != null ? v.compare_at_price : p.compare_at_price;
    const url = (p.url || '/products/' + p.handle).split('?')[0];
    const title = esc(p.title);
    const img = p.featured_image
      ? `<img src="${esc(imgUrl(p.featured_image, 300))}" alt="${title}" loading="lazy" width="150" height="150">`
      : '';
    const add =
      single && p.available
        ? `<button type="button" class="raf-rec__add" data-variant="${v.id}" aria-label="Add ${title} to cart">${PLUS}</button>`
        : `<a class="raf-rec__add" href="${esc(url)}" aria-label="Choose options for ${title}">${PLUS}</a>`;
    const cmp =
      box.dataset.showCompare === 'true' && compare > price
        ? `<s class="raf-rec__compare">${esc(formatMoney(compare, amountOnly(format)))}</s>`
        : '';
    return `<article class="raf-rec"><div class="raf-rec__media"><a class="raf-rec__img" href="${esc(url)}" tabindex="-1" aria-hidden="true">${img}</a>${add}</div><p class="raf-rec__price">${esc(formatMoney(price, format))}</p>${cmp}<a class="raf-rec__name" href="${esc(url)}">${title}</a></article>`;
  }
  async function loadRecs(box) {
    if (box.dataset.rafInit) return;
    box.dataset.rafInit = '1';
    const hasFallback = !!box.querySelector('.raf-rec');
    if (box.dataset.source !== 'auto' || !box.dataset.productId) {
      box.hidden = !hasFallback;
      return;
    }
    const pid = box.dataset.productId;
    const intent = box.dataset.intent || 'related';
    const key = pid + '|' + intent;
    let products = recCache.get(key);
    if (!products) {
      try {
        const res = await fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(pid)}&limit=10&intent=${intent}`, {
          headers: { Accept: 'application/json' },
        });
        products = res.ok ? (await res.json()).products || [] : [];
      } catch (e) {
        products = [];
      }
      recCache.set(key, products);
    }
    if (!box.isConnected) return;
    const exclude = box.dataset.exclude || '';
    const hideSold = box.dataset.hideSoldout === 'true';
    const limit = Number(box.dataset.limit) || 8;
    const list = products
      .filter((p) => !exclude.includes(',' + p.id + ',') && (!hideSold || p.available))
      .slice(0, limit);
    if (!list.length) {
      box.hidden = !hasFallback;
      return;
    }
    const scroller = box.querySelector('[data-raf-recs-list]');
    if (scroller) scroller.innerHTML = list.map((p) => recCard(p, box)).join('');
    box.hidden = false;
  }

  /* ------------------------------------------------------------ init + re-init after re-render */
  function init() {
    document.querySelectorAll('cart-drawer.raf-drawer').forEach((drawer) => {
      updateDelivery(drawer);
      drawer.querySelectorAll('[data-raf-recs]').forEach(loadRecs);
    });
  }
  let pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      init();
    });
  }
  function observe() {
    document.querySelectorAll('cart-drawer.raf-drawer').forEach((drawer) => {
      if (drawer.dataset.rafObserved) return;
      drawer.dataset.rafObserved = '1';
      new MutationObserver((records) => {
        // react only when new drawer markup arrived (not to our own text updates)
        const fresh = records.some((r) =>
          [...r.addedNodes].some(
            (n) => n.nodeType === 1 && (n.matches?.('[data-raf-delivery],[data-raf-recs],.drawer__inner,#CartDrawer') || n.querySelector?.('[data-raf-recs]:not([data-raf-init]),[data-raf-delivery]'))
          )
        );
        if (fresh) schedule();
      }).observe(drawer, { childList: true, subtree: true });
    });
  }
  const start = () => {
    observe();
    init();
    setInterval(() => document.querySelectorAll('cart-drawer.raf-drawer').forEach(updateDelivery), 60000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) document.querySelectorAll('cart-drawer.raf-drawer').forEach(updateDelivery);
    });
    window.addEventListener('focus', () => document.querySelectorAll('cart-drawer.raf-drawer').forEach(updateDelivery));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* ------------------------------------------------------------ fallback refresh */
  async function refreshDrawer(drawer) {
    const res = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Unable to refresh cart');
    const sections = await res.json();
    const parse = (html, sel) => new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
    const freshInner = sections['cart-drawer'] && parse(sections['cart-drawer'], '.drawer__inner');
    const inner = drawer.querySelector('.drawer__inner');
    if (freshInner && inner) inner.innerHTML = freshInner.innerHTML;
    drawer.classList.remove('is-empty');
    const bubble = document.getElementById('cart-icon-bubble');
    const freshBubble = sections['cart-icon-bubble'] && parse(sections['cart-icon-bubble'], '#cart-icon-bubble');
    if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;
  }

  /* RAF 2026: legacy minus-removes handler superseded by drawer-scoped cart controls below. */

  /* ------------------------------------------------------------ clicks */
  let busy = false;
  document.addEventListener('click', async (event) => {
    const add = event.target.closest('cart-drawer .raf-rec__add[data-variant]');
    if (add) {
      event.preventDefault();
      if (busy) return;
      busy = true;
      add.classList.add('is-loading');
      add.setAttribute('aria-busy', 'true');
      const drawer = add.closest('cart-drawer');
      const status = add.closest('[data-raf-recs]')?.querySelector('.raf-recs__status');
      if (status) status.textContent = '';
      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(add.dataset.variant), quantity: 1 }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add this product');
        if (drawer && typeof drawer.renderContents === 'function' && data.sections && data.sections['cart-drawer']) {
          drawer.renderContents(data);
        } else if (drawer) {
          await refreshDrawer(drawer);
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'raf-drawer', cartData: data });
        }
      } catch (err) {
        if (status) status.textContent = err.message;
        console.error('[RAF drawer]', err);
      } finally {
        busy = false;
        add.classList.remove('is-loading');
        add.removeAttribute('aria-busy');
        schedule();
      }
      return;
    }

    const wa = event.target.closest('cart-drawer .raf-wa');
    if (!wa) return;
    event.preventDefault();
    if (busy) return;
    busy = true;
    const phone = (wa.dataset.phone || '').replace(/\D/g, '');
    let msg = wa.dataset.message || 'Hello! I would like to order:';
    const tab = window.open('about:blank', '_blank');
    try {
      if (wa.dataset.cart === 'true') {
        const res = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Unable to load the current cart');
        {
          const cart = await res.json();
          if (!cart.item_count) throw new Error('Your cart is empty');
          const money = (c) => (c / 100).toFixed(2) + ' ' + cart.currency;
          msg +=
            '\n\n' +
            cart.items
              .map(
                (item) =>
                  `${item.product_title}${item.variant_title && item.variant_title !== 'Default Title' ? ' (' + item.variant_title + ')' : ''} × ${item.quantity} — ${money(item.final_line_price)}\n${location.origin}${item.url}`
              )
              .join('\n\n') +
            `\n\nTotal: ${money(cart.total_price)}`;
          msg += '\n\nPlease confirm availability, delivery charges (if any), and COD option. Thanks!';
        }
      }
      const href = 'https://wa.me/' + phone + '?text=' + encodeURIComponent(msg);
      if (tab) tab.location.href = href;
      else window.location.href = href;
    } catch (err) {
      console.error('[RAF drawer]', err);
      if (tab && !tab.closed) tab.close();
      alert(err.message || 'Unable to prepare WhatsApp message. Please try again.');
    } finally {
      busy = false;
    }
  });
})();

// RAF 2026 cart drawer controls: direct line-key updates, no competing handlers.
(()=>{if(window.__rafCartControls)return;window.__rafCartControls=true;
let busy=false;const root=()=>window.Shopify?.routes?.root||'/';
async function update(row,quantity){if(busy)return;const drawer=row.closest('cart-drawer.raf-drawer');const rem=row.querySelector('cart-remove-button');const inp=row.querySelector('.quantity__input');const key=rem?.dataset.itemkey;const line=Number(inp?.dataset.index||rem?.dataset.index);if(!drawer||(!key&&!line))return;busy=true;drawer.setAttribute('aria-busy','true');
try{const res=await fetch(root()+'cart/change.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({id:key||line,quantity})});const cart=await res.json();if(!res.ok||cart.errors)throw Error(cart.description||'Cart update failed');
const sectionsRes=await fetch(root()+'?sections=cart-drawer,cart-icon-bubble',{credentials:'same-origin'});if(!sectionsRes.ok)throw Error('Cart refresh failed');const sections=await sectionsRes.json();const dom=(html,sel)=>new DOMParser().parseFromString(html,'text/html').querySelector(sel);
const current=drawer.querySelector('#CartDrawer');const fresh=sections['cart-drawer']&&dom(sections['cart-drawer'],'#CartDrawer');if(!current||!fresh)throw Error('Cart drawer section missing');current.innerHTML=fresh.innerHTML;drawer.classList.toggle('is-empty',cart.item_count===0);drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty',cart.item_count===0);
const icon=document.querySelector('#cart-icon-bubble');const updatedIcon=sections['cart-icon-bubble']&&dom(sections['cart-icon-bubble'],'#cart-icon-bubble');if(icon&&updatedIcon)icon.innerHTML=updatedIcon.innerHTML;
document.dispatchEvent(new CustomEvent('raf:cart-updated',{detail:{cart}}));
}catch(error){console.error('[RAF cart fix]',error);const message=drawer.querySelector('#CartDrawer-CartErrors');if(message)message.textContent=error.message||'Unable to update cart';}finally{busy=false;drawer.removeAttribute('aria-busy');}}
document.addEventListener('click',event=>{const btn=event.target.closest('cart-drawer.raf-drawer .cart-item .quantity__button,cart-drawer.raf-drawer .cart-item cart-remove-button');if(!btn)return;event.preventDefault();event.stopImmediatePropagation();if(busy||btn.disabled)return;const row=btn.closest('.cart-item');if(btn.matches('cart-remove-button')){update(row,0);return;}const inp=row.querySelector('.quantity__input');if(!inp)return;const step=Math.max(1,Number(inp.step)||1);const now=Number(inp.value)||0;const min=Math.max(1,Number(inp.dataset.min)||1);const max=inp.max?Number(inp.max):Infinity;const next=btn.name==='plus'?Math.min(max,now+step):now-step;if(next<min){if(btn.name==='minus'&&btn.closest('[data-raf-minus-removes]'))update(row,0);return;}if(next!==now)update(row,next);},true);
document.addEventListener('change',event=>{const inp=event.target.closest('cart-drawer.raf-drawer .cart-item .quantity__input');if(!inp)return;event.stopImmediatePropagation();const val=Number(inp.value),min=Math.max(1,Number(inp.dataset.min)||1);if(!Number.isInteger(val)||val<min){inp.value=inp.defaultValue;return;}update(inp.closest('.cart-item'),val);},true);
})();
